import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import styles from '@/styles/superstack/base/MModal.module.scss';
import { ButtonBlock } from '@/src/component/superstack/base/Button';
import IconArrowLeft from '@/asset/superstack/svgs/icon-arrow-left.svg';
import IconHamburger from '@/asset/superstack/svgs/icon-hamburger.svg';
import { MEmptySelectedStackBox } from '@/src/component/superstack/base/Box';
import { useRecoilState } from 'recoil';
import MobileSideBarOpenState from '@/src/store/superstack/MobileSideBarOpenState';
import { toggleSideBar } from '@/src/utils/ss_utils';
import { useEffect } from 'react';

const MSSModal = ({
  isOpen,
  onClose,
  onSubmit,
  onReset,
  btnSubmitName = '적용하기',
  btnCloseName = '취소',
  btnResetName = '초기화',
  customStyle,
  title,
  BottomBoxChildren = null,
  hasCloseBtn = true,
  hasResetBtn = false,
  featureBtn = {},
  hasHeaderMenu = true,
  ...props
}) => {
  const [mobileSideBarOpen, setMobileSideBarOpen] = useRecoilState(MobileSideBarOpenState);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
      window.ChannelIO && window.ChannelIO('shutdown');
    }
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen]);

  const cx = classNames.bind(styles);
  const defaultStyle = {
    overlay: {},
    content: {
      'box-shadow': '0px 10px 37px rgba(0, 0, 0, 0.15)',
      border: 'none',
      margin: 'auto',
      padding: 0,
      position: 'static',
      width: '100%',
      height: '100%',
      zIndex: 10,
    },
  };
  const { overlay = {}, content = {} } = customStyle;
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      isBottomBox={!!BottomBoxChildren}
      style={{
        overlay: { ...defaultStyle.overlay, ...overlay },
        content: { ...defaultStyle.content, ...content },
      }}
      preventScroll={true}
    >
      <div className={cx('ss-modal')}>
        {hasHeaderMenu ? (
          <header className={cx('ss-modal-header')}>
            <>
              <div className={cx('icon-arrow')} onClick={onClose}>
                <IconArrowLeft width={8} height={16} fill="#000000" />
              </div>
              {title}
              <IconHamburger
                width={24}
                height={24}
                onClick={() => {
                  toggleSideBar(mobileSideBarOpen, setMobileSideBarOpen);
                }}
              />
            </>
          </header>
        ) : (
          <header className={cx(['ss-modal-header', 'no-menu'])}>
            <>{title}</>
          </header>
        )}
        <div className={cx('ss-modal-body', BottomBoxChildren ? 'ss-modal-has-bottom-box' : '')}>
          {props.children}
        </div>
        <div className={cx('ss-modal-footer')}>
          {BottomBoxChildren && (
            <MBottomBox filteredItems={props.filteredItems} title={title}>
              {BottomBoxChildren}
            </MBottomBox>
          )}
          <div className={cx('btn-wrapper')}>
            <ButtonBlock
              size="xxl"
              mode="filled"
              onClick={() => {
                const submitted = onSubmit && onSubmit();
                if (submitted !== false) {
                  onClose();
                }
              }}
            >
              <div style={{ whiteSpace: 'nowrap' }}>{btnSubmitName}</div>
            </ButtonBlock>
            {Object.keys(featureBtn).length > 0 && (
              <ButtonBlock
                size={featureBtn.size}
                mode={featureBtn.mode}
                onClick={() => {
                  featureBtn.featureFn();
                }}
              >
                {featureBtn.content}
              </ButtonBlock>
            )}
            {hasCloseBtn && (
              <ButtonBlock
                size="xxl"
                mode="m-cancel"
                onClick={() => {
                  onClose && onClose();
                }}
              >
                {btnCloseName}
              </ButtonBlock>
            )}
            {hasResetBtn && (
              <ButtonBlock
                size="xxl"
                mode="m-cancel"
                onClick={() => {
                  onReset && onReset();
                }}
              >
                {btnResetName}
              </ButtonBlock>
            )}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

const MSSModalFloat = ({
  isOpen,
  onClose,
  onSubmit,
  btnSubmitName = '적용하기',
  btnCloseName = '취소',
  customStyle,
  title,
  hasCloseBtn = true,
  featureBtn = {},
  ...props
}) => {
  const cx = classNames.bind(styles);
  const defaultStyle = {
    overlay: {},
    content: {
      'box-shadow': '0px 10px 37px rgba(0, 0, 0, 0.15)',
      borderRadius: '8px',
      margin: 'auto',
      marginTop: '120px',
      padding: 0,
      position: 'static',
      width: '90%',
      maxHeight: '70%',
      zIndex: 10,
    },
  };
  const { overlay = {}, content = {} } = customStyle;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { ...defaultStyle.overlay, ...overlay },
        content: { ...defaultStyle.content, ...content },
      }}
      preventScroll={true}
    >
      <div className={cx(['ss-modal', 'ss-modal-float'])}>
        <header className={cx('ss-modal-header')}>{title}</header>
        <div className={cx('ss-modal-body')}>{props.children}</div>
        <div className={cx('ss-modal-footer')}>
          <div className={cx('btn-wrapper')}>
            <ButtonBlock
              size="xxl"
              mode="filled"
              onClick={() => {
                const submitted = onSubmit && onSubmit();
                if (submitted !== false) {
                  onClose();
                }
              }}
            >
              {btnSubmitName}
            </ButtonBlock>
            {Object.keys(featureBtn).length > 0 && (
              <ButtonBlock
                size={featureBtn.size}
                mode={featureBtn.mode}
                onClick={() => {
                  featureBtn.featureFn();
                }}
              >
                {featureBtn.content}
              </ButtonBlock>
            )}
            {hasCloseBtn && (
              <ButtonBlock size="xxl" mode="m-cancel" onClick={onClose}>
                {btnCloseName}
              </ButtonBlock>
            )}
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

const MSSPartialBlankModal = ({ isOpen, onClose, customStyle, ...props }) => {
  const cx = classNames.bind(styles);
  const defaultStyle = {
    overlay: {},
    content: {
      'box-shadow': '0px 10px 37px rgba(0, 0, 0, 0.15)',
      border: 'none',
      borderRadius: '8px',
      margin: 'auto',
      padding: 0,
      position: 'static',
      top: '20px',
      width: '310px',
      height: 'auto',
      zIndex: 10,
    },
  };
  const { overlay = {}, content = {} } = customStyle;
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { ...defaultStyle.overlay, ...overlay },
        content: { ...defaultStyle.content, ...content },
      }}
      preventScroll={true}
    >
      <div className={cx('ss-partial-modal')}>
        <div className={cx('ss-modal-body')}>{props.children}</div>
      </div>
    </ReactModal>
  );
};

const MBottomBox = ({ filteredItems = [], title, ...props }) => {
  const hasItem =
    (Array.isArray(filteredItems) && filteredItems.length > 0) ||
    Object.keys(filteredItems).length > 0;
  return (
    <div>
      {hasItem ? (
        <div style={{ padding: '16px 24px 0px 24px' }}>
          <div
            style={{
              height: '124px',
              padding: '12px',
              backgroundColor: '#FAFAFA',
              overflowY: 'auto',
            }}
          >
            {props.children}
          </div>
        </div>
      ) : (
        <MEmptySelectedStackBox title={title} />
      )}
    </div>
  );
};

MSSModal.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  customStyle: PropTypes.object,
};

MSSModal.defaultProps = {
  isOpen: true,
  customStyle: {
    overlay: {},
    content: {},
  },
};

MSSModalFloat.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  customStyle: PropTypes.object,
};

MSSModalFloat.defaultProps = {
  isOpen: true,
  customStyle: {
    overlay: {},
    content: {},
  },
};

export { MSSModal, MSSModalFloat, MSSPartialBlankModal };
